"use client"

import type React from "react"

import { Card, CardContent } from "@/components/ui/card"
import { Text, Center, VStack, HStack, Box } from "@chakra-ui/react"
import { useTranslations } from "next-intl"
import { Skeleton } from "@/components/ui/skeleton"
import useKeywordAnalyticsStore from "@/store/KeywordAnalyticsStore"
import { useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
const moment = require("moment")

interface ReplyToPost {
  id: string
  username: string
  caption: string
  permalink: string
  postCreatedAt: string
}

interface ThreadPost {
  id: string
  date: Date
  username: string
  caption: string
  permalink: string
  postCreatedAt: string
  replyToPost?: ReplyToPost
  containsKeyword: boolean
  keywordText: string
  tagHeader?: string
}

const ThreadPostCard = ({
  post,
  showKeywordBadge = true,
}: {
  post: ThreadPost
  showKeywordBadge?: boolean
}) => {
  const tDetail = useTranslations("KeywordDetail")
  const t = useTranslations("KeywordDetail.ThreadPosts")
  const [isReplyExpanded, setIsReplyExpanded] = useState(false)

  const trimCaption = (caption: string, maxLength = 100) => {
    if (caption.length <= maxLength) return caption
    return caption.slice(0, maxLength) + "..."
  }

  const handleClick = () => {
    window.open(post.permalink, "_blank")
  }

  const handleReplyClick = (e: React.MouseEvent, permalink: string) => {
    e.stopPropagation()
    window.open(permalink, "_blank")
  }

  const toggleReplyExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsReplyExpanded(!isReplyExpanded)
  }

  return (
    <Card
      className={`cursor-pointer rounded-[14px] border border-[#ced0d5] bg-white shadow-none transition-[box-shadow,border-color] duration-150 hover:border-[#b2b4ba] hover:shadow-[0_4px_14px_rgba(0,0,0,0.06)] ${
        isReplyExpanded ? "h-[320px]" : "h-[240px]"
      }`}
      onClick={handleClick}>
      <CardContent className="h-full p-5">
        <VStack align="leading" spacing={3} width="100%">
          <Box height="160px" width="100%">
            <VStack align="leading" spacing={2} width="100%">
              <VStack align="leading" spacing={0}>
                <HStack
                  width="100%"
                  justifyContent="space-between"
                  alignItems="center">
                  <Text fontSize="md" fontWeight="medium" color="text.dark">
                    @{post.username}
                  </Text>
                  {showKeywordBadge && (
                    <Badge
                      variant="secondary"
                      className="w-fit shrink-0 border-primary/20 bg-primary/10 text-xs text-primary hover:bg-primary/10">
                      {post.keywordText}
                    </Badge>
                  )}
                </HStack>
                <VStack spacing={1} align="left">
                  <HStack>
                    <Text fontSize="sm" color="text.default">
                      {moment(post.postCreatedAt).format(t("post.postedAt"))}
                    </Text>
                    {post.tagHeader && (
                      <Text fontSize="sm" color="text.semidark">
                        #{post.tagHeader}
                      </Text>
                    )}
                  </HStack>
                  {post.containsKeyword === false && (
                    <Badge className="w-fit" variant="outline">
                      {tDetail("Message.notContainKeyword")}
                    </Badge>
                  )}
                </VStack>
              </VStack>
              <Text fontSize="md" color="text.semidark" noOfLines={4}>
                {trimCaption(post.caption)}
              </Text>
            </VStack>
          </Box>

          {post.replyToPost && (
            <>
              {!isReplyExpanded ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-sm text-gray-500"
                  onClick={toggleReplyExpanded}>
                  查看原文
                  <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              ) : (
                <Box
                  width="100%"
                  p={3}
                  borderRadius="md"
                  bg="gray.50"
                  className="hover:bg-gray-100"
                  height="120px"
                  onClick={(e) =>
                    handleReplyClick(e, post.replyToPost!.permalink)
                  }>
                  <VStack align="leading" spacing={1}>
                    <HStack width="100%" justifyContent="space-between">
                      <Text fontSize="xs" color="text.light" fontStyle="italic">
                        {t("post.repliedTo")}
                      </Text>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 p-0"
                        onClick={toggleReplyExpanded}>
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium" color="text.dark">
                        @{post.replyToPost.username}
                      </Text>
                      <Text fontSize="xs" color="text.light">
                        {moment(post.replyToPost.postCreatedAt).format(
                          t("post.postedAt"),
                        )}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="text.semidark" noOfLines={2}>
                      {trimCaption(post.replyToPost.caption)}
                    </Text>
                  </VStack>
                </Box>
              )}
            </>
          )}
        </VStack>
      </CardContent>
    </Card>
  )
}

const LoadingCard = () => (
  <Card className="h-[240px] rounded-[14px] border border-[#ced0d5] bg-white shadow-none">
    <CardContent className="p-5">
      <VStack align="leading" spacing={4}>
        <VStack align="leading" spacing={2}>
          <Skeleton className="h-5 w-[120px]" />
          <Skeleton className="h-4 w-[100px]" />
        </VStack>
        <VStack align="leading" spacing={2}>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </VStack>
      </VStack>
    </CardContent>
  </Card>
)

const ThreadPostList = ({
  posts,
  showKeywordBadge = true,
}: {
  posts: ThreadPost[]
  showKeywordBadge?: boolean
}) => {
  const t = useTranslations("KeywordDetail.ThreadPosts")
  const isLoading = useKeywordAnalyticsStore((state) => state.isInitialLoading)

  const loadingView = (
    <div className="grid gap-[18px] md:grid-cols-2 lg:grid-cols-4">
      {[...Array(6)].map((_, index) => (
        <LoadingCard key={index} />
      ))}
    </div>
  )

  const cardElement = (
    <div className="grid gap-[18px] md:grid-cols-2 lg:grid-cols-4">
      {posts.map((post) => (
        <ThreadPostCard
          key={post.id}
          post={post}
          showKeywordBadge={showKeywordBadge}
        />
      ))}
    </div>
  )

  const emptyView = (
    <Center>
      <Text as="b" fontSize="xl">
        {t("emptyState")}
      </Text>
    </Center>
  )

  if (isLoading) {
    return loadingView
  }
  return posts.length > 0 ? cardElement : emptyView
}

export default ThreadPostList
